import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { CreditCard, Crown, Calendar, RefreshCw, AlertTriangle, CheckCircle2, ShieldCheck } from "lucide-react";

export default function ManageSubscription() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription");
      setSub(data);
    } catch { setSub(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setActing(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription set to cancel at period end");
      setConfirmCancel(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
    finally { setActing(false); }
  };

  const resume = async () => {
    setActing(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed — renews automatically");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resume"); }
    finally { setActing(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";

  if (loading) return <div className="text-slate-400">Loading subscription…</div>;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/20 grid place-items-center">
          <CreditCard className="w-6 h-6 text-blue-300" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Manage Subscription</h1>
          <p className="text-slate-400 text-sm mt-0.5">View your plan, renewal date and cancel anytime.</p>
        </div>
      </div>

      {user?.plan !== "pro" ? (
        <UpgradePrompt title="You're on the Free plan" description="Upgrade to Pro (€3.99/month) to manage a subscription and unlock everything." />
      ) : (
        <>
          <div className="bg-gradient-to-br from-blue-950/50 to-[#0F172A] border border-blue-500/30 rounded-2xl p-6" data-testid="subscription-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/25 grid place-items-center">
                  <Crown className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <div className="font-display text-xl font-bold">Trading Academy Pro</div>
                  <div className="text-sm text-slate-400 font-mono">
                    €{Number(sub?.amount ?? 3.99).toFixed(2)} / {sub?.interval || "month"}
                  </div>
                </div>
              </div>
              <span data-testid="sub-status" className={`text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 ${sub?.cancel_at_period_end ? "text-amber-300 bg-amber-500/15 border border-amber-500/30" : "text-emerald-300 bg-emerald-500/15 border border-emerald-500/30"}`}>
                {sub?.cancel_at_period_end ? "Cancels soon" : "Active"}
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-mono mb-1.5">
                  <Calendar className="w-3.5 h-3.5" /> {sub?.cancel_at_period_end ? "Access until" : "Next renewal"}
                </div>
                <div className="font-semibold text-slate-100" data-testid="renewal-date">{fmtDate(sub?.renewal_date)}</div>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-mono mb-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> Status
                </div>
                <div className="font-semibold text-slate-100 capitalize">{sub?.status || "active"}</div>
              </div>
            </div>
          </div>

          {sub?.cancel_at_period_end ? (
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-200">Your subscription is set to cancel on <span className="font-semibold">{fmtDate(sub?.renewal_date)}</span>. You'll keep Pro access until then.</p>
                  <button data-testid="resume-sub-btn" onClick={resume} disabled={acting} className="mt-3 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-60">
                    <RefreshCw className="w-4 h-4" /> Resume subscription
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-[#0F172A] border border-slate-800 rounded-xl p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold text-slate-100">Cancel subscription</div>
                  <p className="text-sm text-slate-400 mt-0.5">You'll keep Pro until the end of the current billing period. No further charges.</p>
                </div>
                {!confirmCancel ? (
                  <button data-testid="cancel-sub-btn" onClick={() => setConfirmCancel(true)} className="text-sm font-semibold text-red-400 hover:text-red-300 border border-red-500/30 rounded-lg px-4 py-2 transition-colors">
                    Cancel anytime
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setConfirmCancel(false)} className="text-sm text-slate-400 hover:text-slate-200 px-3 py-2">Keep Pro</button>
                    <button data-testid="confirm-cancel-btn" onClick={cancel} disabled={acting} className="text-sm font-semibold text-white bg-red-600 hover:bg-red-500 rounded-lg px-4 py-2 transition-colors disabled:opacity-60">
                      {acting ? "Cancelling…" : "Confirm cancel"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 text-xs text-slate-500">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-slate-600" />
            <span>Billing is handled securely by Stripe. This is a test-mode subscription — no real money is charged.</span>
          </div>
        </>
      )}
    </div>
  );
}
